import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const InteractiveTimeline = () => {
  const [activeNode, setActiveNode] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const timelineData = [
    {
      id: 1,
      year: '2019',
      title: 'The Foundation',
      subtitle: 'Technical Support',
      description: `Kicked off my tech journey with a deep dive into hardware and networks. Learned how computers speak in binary, and how those tiny ones and zeros power everything we see. It was the year I realized technology wasn’t just tools—it was a whole language to master.`,
      icon: 'Cpu',
      color: '#00d4ff',
      achievements: ['Hardware & Networking', 'Binary Code Fundamentals', 'Computer Communication Systems'],
      philosophy: 'A strong foundation makes innovation possible.'
    },
    {
      id: 2,
      year: '2020',
      title: 'The First Leap',
      subtitle: 'Data Administration',
      description: `From spreadsheets to servers—this year was about mastering the Microsoft Office Suite and transforming paper records into digital gold at the Western Cape Department of Social Development. Somewhere between hackathons and late-night coding, I knew programming had me hooked.`,
      icon: 'Database',
      color: '#00ff88',
      achievements: ['Microsoft Office Suite', 'Data Digitization', 'Hackathon Participation'],
      philosophy: 'Every great journey starts with a small leap forward.'
    },
    {
      id: 3,
      year: '2021',
      title: 'The Breakthrough',
      subtitle: 'Systems Development',
      description: `This was my "level-up" year—front-end, back-end, and everything in between. HTML, CSS, JavaScript with React and Next.js, Java, Python with Django—if it could be built, I wanted to build it. Each project became a stepping stone, pushing my skills further than I thought possible.`,
      icon: 'Code',
      color: '#8844ff',
      achievements: ['HTML, CSS, JavaScript', 'React & Next.js', 'Java & Python (Django)', 'Bootstrap Projects'],
      philosophy: 'Practice turns knowledge into skill.'
    },
    {
      id: 4,
      year: '2022',
      title: 'The Deep Dive',
      subtitle: 'APIs, Backend & E-Commerce',
      description: `Dove headfirst into APIs, backend architecture, and e-commerce development. Built websites on WordPress and Wix, experimented with Glide for mobile apps, and kept my finger on the pulse of tech trends. This was also the spark that ignited my AI journey.`,
      icon: 'Globe',
      color: '#ffaa00',
      achievements: ['API Integrations', 'Backend Development', 'WordPress & Wix', 'Low-Code Mobile Apps (Glide)'],
      philosophy: 'Adaptability is the ultimate developer skill.'
    },
    {
      id: 5,
      year: '2023',
      title: 'The Leadership Step',
      subtitle: 'Tech Champ / Junior Tech Champ',
      description: `Stepped into a role that was as much about people as it was about tech. Guided candidates, solved problems, and made sure everyone had what they needed to succeed. My AI skills kept growing, this time with real-world impact.`,
      icon: 'Users',
      color: '#ff6b35',
      achievements: ['Technical Mentorship', 'Troubleshooting Support', 'AI Learning in Practice'],
      philosophy: 'Leadership is service through knowledge.'
    },
    {
      id: 6,
      year: '2024',
      title: 'The Expansion',
      subtitle: 'AI, Deep Learning & Automation',
      description: `Took a bold leap into deep learning, machine learning, and automation. Learned to craft smarter prompts, explored AI tools, and picked up automation certifications with platforms like Workato. Flexibility wasn’t just a skill—it was a requirement.`,
      icon: 'Brain',
      color: '#ff4081',
      achievements: ['Deep Learning', 'Machine Learning', 'Prompt Engineering', 'Automation with Workato'],
      philosophy: 'Technology evolves—so must we.'
    },
    {
      id: 7,
      year: '2025',
      title: 'The Ongoing Journey',
      subtitle: 'New Challenges & Collaborations',
      description: `Still pushing forward, chasing challenges that stretch my abilities and collaborating on projects that spark innovation. The journey’s far from over—the best chapters are still being written.`,
      icon: 'Rocket',
      color: '#29b6f6',
      achievements: ['Continuous Learning', 'Collaboration', 'Innovation'],
      philosophy: 'The best is always yet to come.'
    }
  ];

  return (
    <div className="w-full max-w-6xl mx-auto">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-orbitron font-bold text-3xl text-foreground mb-4">
          Evolution Timeline
        </h2>
        <p className="text-text-secondary font-inter max-w-2xl mx-auto">
          A multi-dimensional journey through the transformation from traditional coder to digital visionary
        </p>
      </motion.div>

      {/* Mobile View - Vertical Scroll */}
      {isMobile ? (
        <div className="space-y-4">
          {timelineData.map((node, index) => (
            <motion.div
              key={node.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true, margin: "-100px" }}
              className="relative"
            >
              <motion.div
                className="glass-morphism rounded-xl p-6 cyber-border cursor-pointer cyber-hover"
                whileHover={{ scale: 1.02 }}
                onClick={() => setActiveNode(activeNode === node.id ? null : node.id)}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center cyber-glow flex-shrink-0"
                    style={{ backgroundColor: `${node.color}20`, borderColor: node.color }}
                  >
                    <Icon name={node.icon} size={24} style={{ color: node.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-orbitron font-bold text-lg text-foreground">
                        {node.title}
                      </h3>
                      <span className="font-orbitron font-bold text-lg text-foreground">
                        {node.year}
                      </span>
                    </div>
                    <p className="text-xs font-mono" style={{ color: node.color }}>
                      {node.subtitle}
                    </p>
                  </div>
                </div>

                {activeNode === node.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4 mt-4 pt-4 border-t border-border"
                  >
                    <p className="text-text-secondary font-inter text-sm leading-relaxed">
                      {node.description}
                    </p>
                    <div>
                      <h4 className="font-inter font-semibold text-foreground text-sm mb-2">Key Achievements</h4>
                      <div className="space-y-2">
                        {node.achievements.map((achievement, idx) => (
                          <div
                            key={idx}
                            className="flex items-center space-x-2 text-sm"
                          >
                            <Icon name="CheckCircle" size={14} style={{ color: node.color }} />
                            <span className="text-text-secondary">{achievement}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <blockquote className="italic text-text-secondary font-inter text-sm border-l-2 pl-3" style={{ borderColor: node.color }}>
                      "{node.philosophy}"
                    </blockquote>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          ))}
        </div>
      ) : (
        /* Desktop View - Timeline */
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-brand-cyan via-brand-purple to-brand-green opacity-30"></div>

          {/* Timeline Nodes */}
          <div className="space-y-16">
            {timelineData.map((node, index) => (
              <motion.div
                key={node.id}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                {/* Content Card */}
                <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}>
                  <motion.div
                    className="glass-morphism rounded-xl p-6 cyber-border cursor-pointer cyber-hover"
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setActiveNode(activeNode === node.id ? null : node.id)}
                  >
                    <div className="flex items-center space-x-3 mb-4">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center cyber-glow flex-shrink-0"
                        style={{ backgroundColor: `${node.color}20`, borderColor: node.color }}
                      >
                        <Icon name={node.icon} size={24} style={{ color: node.color }} />
                      </div>
                      <div>
                        <h3 className="font-orbitron font-bold text-xl text-foreground">
                          {node.title}
                        </h3>
                        <p className="text-sm font-mono" style={{ color: node.color }}>
                          {node.subtitle}
                        </p>
                      </div>
                    </div>

                    <p className="text-text-secondary font-inter text-sm leading-relaxed mb-4">
                      {node.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-text-tertiary">
                        Click to expand
                      </span>
                      <motion.div
                        animate={{ rotate: activeNode === node.id ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Icon name="ChevronDown" size={16} className="text-text-secondary" />
                      </motion.div>
                    </div>
                  </motion.div>
                </div>

                {/* Timeline Node */}
                <div className="absolute left-1/2 transform -translate-x-1/2 z-10">
                  <motion.div
                    className="w-8 h-8 rounded-full border-4 border-background flex items-center justify-center cyber-glow"
                    style={{ backgroundColor: node.color }}
                    whileHover={{ scale: 1.2 }}
                    animate={{
                      boxShadow: activeNode === node.id 
                        ? `0 0 30px ${node.color}80` 
                        : `0 0 20px ${node.color}40`
                    }}
                  >
                    <span className="font-mono font-bold text-xs text-background">
                      {node.year.slice(-2)}
                    </span>
                  </motion.div>
                </div>

                {/* Year Label */}
                <div className={`w-5/12 ${index % 2 === 0 ? 'pl-8' : 'pr-8'} flex ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                  <div className="text-center">
                    <div className="font-orbitron font-bold text-2xl text-foreground">
                      {node.year}
                    </div>
                    <div className="w-16 h-px bg-gradient-to-r from-transparent via-brand-cyan to-transparent mt-2"></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Desktop Modal for Expanded Details */}
      {!isMobile && (
        <AnimatePresence>
          {activeNode && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-md"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveNode(null)}
            >
              <motion.div
                className="glass-morphism rounded-2xl p-8 max-w-2xl w-full cyber-border-active cyber-glow-intense"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                {(() => {
                  const node = timelineData.find(n => n.id === activeNode);
                  return (
                    <div>
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-4">
                          <div
                            className="w-16 h-16 rounded-xl flex items-center justify-center cyber-glow"
                            style={{ backgroundColor: `${node.color}20`, borderColor: node.color }}
                          >
                            <Icon name={node.icon} size={32} style={{ color: node.color }} />
                          </div>
                          <div>
                            <h3 className="font-orbitron font-bold text-2xl text-foreground">
                              {node.title}
                            </h3>
                            <p className="font-mono" style={{ color: node.color }}>
                              {node.year} • {node.subtitle}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setActiveNode(null)}
                          className="p-2 rounded-lg hover:bg-surface/50 transition-colors duration-200"
                        >
                          <Icon name="X" size={24} className="text-text-secondary" />
                        </button>
                      </div>

                      <div className="space-y-6">
                        <div>
                          <h4 className="font-inter font-semibold text-foreground mb-3">Journey Details</h4>
                          <p className="text-text-secondary leading-relaxed">
                            {node.description}
                          </p>
                        </div>

                        <div>
                          <h4 className="font-inter font-semibold text-foreground mb-3">Key Achievements</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {node.achievements.map((achievement, idx) => (
                              <div
                                key={idx}
                                className="flex items-center space-x-2 p-2 rounded-lg bg-surface/30"
                              >
                                <Icon name="CheckCircle" size={16} style={{ color: node.color }} />
                                <span className="text-sm text-text-secondary">{achievement}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="border-t border-border pt-4">
                          <h4 className="font-inter font-semibold text-foreground mb-2">Philosophy</h4>
                          <blockquote className="italic text-text-secondary font-inter">
                            "{node.philosophy}"
                          </blockquote>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default InteractiveTimeline;